<template>
  <modal :show="show" @update:show="handleModalClose" class="dark-modal">
    <div class="modal-box">
      <div class="modal-box-header">
        <h2 class="modal-title">{{ modalTitle }}</h2>
      </div>

      <form @submit.prevent="submit">
        <!-- Name Field -->
        <div class="field">
          <label class="label">
            {{ $t('sourceTypes.form.name') }}<span class="required-asterisk">*</span>
            <popover placement="top" width="300px">
              <span slot="trigger" class="icon is-small has-text-info" style="cursor: help;">
                <octicon :icon="info" />
              </span>
              <div class="has-text-left is-size-7">
                {{ $t('sourceTypes.form.nameTooltip') }}
              </div>
            </popover>
          </label>
          <div class="control">
            <input
              v-model="formData.name"
              class="input"
              type="text"
              :class="{ 'is-danger': validationErrors.name, 'is-readonly': isEditMode }"
              :placeholder="$t('sourceTypes.form.namePlaceholder')"
              :disabled="isEditMode"
              :readonly="isEditMode"
              maxlength="30"
              @input="handleNameInput"
              @blur="handleNameBlur"
            >
          </div>
          <p v-if="validationErrors.name" class="help is-danger">
            {{ validationErrors.name }}
          </p>
        </div>

        <!-- Description Field -->
        <div class="field">
          <label class="label">{{ $t('description') }}</label>
          <div class="control">
            <textarea
              v-model="formData.description"
              class="textarea"
              :placeholder="$t('sourceTypes.form.descriptionPlaceholder')"
              maxlength="4096"
              rows="3"
              @input="handleDescriptionInput"
            />
          </div>
          <p class="help">{{ descriptionCharacterCount }}/4096</p>
        </div>

        <!-- Format Field -->
        <div class="field">
          <label class="label">{{ $t('sourceTypes.format') }}</label>
          <div class="control">
            <div class="select is-fullwidth">
              <select v-model="formData.type" required @change="handleTypeChange">
                <option disabled value="">{{ $t('selectDefault') }}</option>
                <option v-for="type in types" :key="type" :value="type">
                  {{ formatTypeOption(type) }}
                </option>
              </select>
            </div>
          </div>
        </div>

        <!-- CSV Fields -->
        <template v-if="formData.type === 'csv'">
          <div class="field">
            <label class="label">
              {{ $t('sourceTypes.form.fieldsList') }}
              <span class="required-asterisk">*</span>
            </label>
            <div class="field is-grouped">
              <div class="control is-expanded">
                <input
                  v-model="field.name"
                  class="input"
                  type="text"
                  :class="{ 'is-danger': validationErrors.field_name_input }"
                  :placeholder="$t('sourceTypes.form.fieldNamePlaceholder')"
                  maxlength="4096"
                  @keyup.enter="fieldAdd"
                  @input="clearFieldError('field_name_input')"
                >
              </div>
              <p class="control">
                <button type="button" class="button is-info" @click.prevent="fieldAdd">
                  {{ $t('sourceTypes.form.addField') }}
                </button>
              </p>
            </div>
            <p v-if="validationErrors.field_name_input" class="help is-danger">
              {{ validationErrors.field_name_input }}
            </p>
            <div v-for="(f, index) in fields" :key="f.name" class="field is-grouped">
              <p class="control is-expanded">
                <input :value="f.name" class="input is-small is-readonly" type="text" readonly>
              </p>
              <p class="control">
                <button type="button" class="button is-danger is-small" @click.prevent="fieldRemove(index)">
                  <octicon :icon="x" />
                </button>
              </p>
            </div>
            <div v-if="validationErrors.csv_fields" class="help has-text-danger">
              {{ validationErrors.csv_fields }}
            </div>
          </div>

          <div class="field">
            <label class="label">
              {{ $t('sourceTypes.form.fieldSeparator') }}
              <span class="required-asterisk">*</span>
              <popover placement="top" width="300px">
                <span slot="trigger" class="icon is-small has-text-info ml-1" style="cursor: help;">
                  <octicon :icon="info" />
                </span>
                <div class="has-text-left is-size-7">
                  {{ $t('sourceTypes.form.fieldSeparatorTooltip') }}
                </div>
              </popover>
            </label>
            <div class="control">
              <input
                v-model="formData.field_delimiter"
                class="input"
                type="text"
                :class="{ 'is-danger': validationErrors.field_delimiter }"
                :placeholder="$t('sourceTypes.form.fieldSeparatorPlaceholder')"
                required
                maxlength="4096"
                @blur="validateConditionalFieldsForm"
                @input="clearFieldError('field_delimiter'); applyCharacterLimitsToForm()"
              >
            </div>
            <p v-if="validationErrors.field_delimiter" class="help is-danger">
              {{ validationErrors.field_delimiter }}
            </p>
          </div>
        </template>

        <!-- Event Separator Field -->
        <div class="field">
          <label class="label">
            {{ $t('sourceTypes.form.eventSeparator') }}
            <span class="required-asterisk">*</span>
            <popover placement="top" width="300px">
              <span slot="trigger" class="icon is-small has-text-info ml-1" style="cursor: help;">
                <octicon :icon="info" />
              </span>
              <div class="has-text-left is-size-7">
                {{ $t('sourceTypes.form.eventSeparatorTooltip') }}
              </div>
            </popover>
          </label>
          <div class="control">
            <input
              v-model="delimiter"
              class="input"
              type="text"
              :class="{ 'is-danger': validationErrors.event_delimiter }"
              :placeholder="$t('sourceTypes.form.eventSeparatorPlaceholder')"
              required
              maxlength="4096"
              @blur="validateConditionalFieldsForm"
              @input="clearFieldError('event_delimiter'); applyCharacterLimitsToForm()"
            >
          </div>
          <p v-if="validationErrors.event_delimiter" class="help is-danger">
            {{ validationErrors.event_delimiter }}
          </p>
        </div>

        <!-- Comment Field -->
        <div class="field">
          <label class="label">
            {{ $t('comment') }}
            <popover placement="top" width="300px">
              <span slot="trigger" class="icon is-small has-text-info ml-1" style="cursor: help;">
                <octicon :icon="info" />
              </span>
              <div class="has-text-left is-size-7">
                {{ $t('sourceTypes.form.commentTooltip') }}
              </div>
            </popover>
          </label>
          <div class="control">
            <input
              v-model="formData.comment"
              class="input"
              type="text"
              :class="{ 'is-danger': validationErrors.comment }"
              :placeholder="$t('sourceTypes.form.commentPlaceholder')"
              maxlength="4096"
              @blur="validateConditionalFieldsForm"
              @input="clearFieldError('comment'); applyCharacterLimitsToForm()"
            >
          </div>
          <p v-if="validationErrors.comment" class="help is-danger">
            {{ validationErrors.comment }}
          </p>
        </div>

        <!-- Custom RegExp Field -->
        <template v-if="formData.type === 'custom'">
          <div class="field">
            <label class="label">
              {{ $t('sourceTypes.form.regexp') }}
              <span class="required-asterisk">*</span>
            </label>
            <div class="control">
              <textarea
                v-model="formData.regex_string"
                class="textarea"
                :class="{ 'is-danger': validationErrors.regex_string }"
                :placeholder="$t('sourceTypes.form.regexpPlaceholder')"
                required
                maxlength="4096"
                @blur="validateConditionalFieldsForm"
                @input="clearFieldError('regex_string'); applyCharacterLimitsToForm()"
              />
            </div>
            <p v-if="validationErrors.regex_string" class="help is-danger">
              {{ validationErrors.regex_string }}
            </p>
          </div>
        </template>

        <!-- Timestamp Mode Field -->
        <div class="field">
          <label class="label">{{ $t('sourceTypes.form.timestampMode') }}</label>
          <div class="control">
            <label class="radio">
              <input
                v-model="formData.time_fill"
                type="radio"
                name="time_option"
                :value="true"
              >
              {{ $t('sourceTypes.form.autoTimestamp') }}
            </label>
          </div>
          <div class="control">
            <label class="radio">
              <input
                v-model="formData.time_fill"
                type="radio"
                name="time_option"
                :value="false"
              >
              {{ $t('sourceTypes.form.manualTimestamp') }}
            </label>
          </div>
        </div>

        <!-- Time Fields -->
        <template v-if="!formData.time_fill">
          <div class="field is-grouped time-fields-group">
            <p class="control is-expanded">
              <label class="label">
                {{ $t('sourceTypes.form.timestampField') }}
                <span class="required-asterisk">*</span>
              </label>
              <input
                v-model="formData.time_field"
                class="input"
                type="text"
                :class="{ 'is-danger': validationErrors.time_field }"
                :placeholder="$t('sourceTypes.form.timestampFieldPlaceholder')"
                required
                maxlength="4096"
                @blur="validateConditionalFieldsForm"
                @input="clearFieldError('time_field'); applyCharacterLimitsToForm()"
              >
            </p>
            <p class="control is-expanded">
              <label class="label">
                {{ $t('sourceTypes.form.timestampFormat') }}
                <span class="required-asterisk">*</span>
              </label>
              <input
                v-model="formData.time_format"
                class="input"
                type="text"
                :class="{ 'is-danger': validationErrors.time_format }"
                :placeholder="$t('sourceTypes.form.timestampFormatPlaceholder')"
                required
                maxlength="4096"
                @blur="validateConditionalFieldsForm"
                @input="clearFieldError('time_format'); applyCharacterLimitsToForm()"
              >
            </p>
          </div>
          <div class="field is-grouped time-errors-group">
            <div class="control is-expanded">
              <p v-if="validationErrors.time_field" class="help is-danger">
                {{ validationErrors.time_field }}
              </p>
            </div>
            <div class="control is-expanded">
              <p v-if="validationErrors.time_format" class="help is-danger">
                {{ validationErrors.time_format }}
              </p>
            </div>
          </div>
        </template>

        <!-- Footer Buttons -->
        <div class="modal-footer">
          <button type="button" class="button cancel-button" @click.prevent="handleCancel" :disabled="isLoading">
            {{ $t('cancel') }}
          </button>
          <button type="submit" class="button is-primary" :class="{'is-loading': isLoading}" :disabled="isLoading">
            {{ $t('confirm') }}
          </button>
        </div>
      </form>
    </div>

    <!-- Cancel Confirmation Modal -->
    <modal :show.sync="showCancelConfirm" class="cancel-confirm-modal">
      <div class="confirm-box">
        <div class="confirm-header">
          <h3>{{ $t('indexes.cancelTitle') }}</h3>
        </div>
        <div class="confirm-body">
          <p>{{ $t('indexes.cancelMessage') }}</p>
        </div>
        <div class="confirm-footer">
          <button
            type="button"
            class="button cancel-button"
            @click="showCancelConfirm = false"
          >
            {{ $t('cancel') }}
          </button>
          <button
            type="button"
            class="button is-primary"
            @click="confirmCancel"
          >
            {{ $t('confirm') }}
          </button>
        </div>
      </div>
    </modal>
  </modal>
</template>

<script>
import { Modal, Popover } from '@cyradar/ui'
import Octicon, { info, x } from 'octicons-vue'
import { validateSourceTypeName, validateConditionalFields, applyCharacterLimits } from '@/utils/sourceTypeValidation'

const types = ['json', 'csv', 'xml', 'syslog', 'cef', 'custom', 'rfc5424', 'linux_syslog', 'vcenter8', 'fortinet', 'f5_asm_key_value']

const uniq = (arr) => {
  return arr.filter((elem, pos, arr) => {
    return arr.indexOf(elem) === pos
  })
}

export default {
  name: 'SourceTypeFormModal',
  components: { Modal, Popover, Octicon },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    isEditMode: {
      type: Boolean,
      default: false
    },
    editItem: {
      type: Object,
      default: null
    },
    existingSourceTypes: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      formData: {
        name: '',
        description: '',
        type: 'json',  // Default to JSON as per UC-1.15
        field_names: '',
        event_delimiter: '\\n',
        field_delimiter: ',',
        comment: '#',
        time_fill: true,
        time_field: '',
        time_format: '',
        regex_string: ''
      },
      field: {
        name: ''
      },
      validationErrors: {
        name: '',
        event_delimiter: '',
        field_delimiter: '',
        comment: '',
        regex_string: '',
        csv_fields: '',
        field_name_input: '',
        time_field: '',
        time_format: ''
      },
      descriptionCharacterCount: 0,
      isLoading: false,
      showCancelConfirm: false
    }
  },
  computed: {
    info() {
      return info
    },
    x() {
      return x
    },
    types() {
      return types
    },
    modalTitle() {
      if (this.isEditMode) {
        return this.$t('sourceTypes.updateTitle', { name: this.formData.name || '' })
      }
      return this.$t('newSourceType')
    },
    delimiter: {
      get() {
        if (!this.formData || !this.formData.event_delimiter) {
          return ''
        }
        return this.escapeNL(this.formData.event_delimiter)
      },
      set(val) {
        const raw = this.unescapeNL(val)
        if (!this.formData) {
          return
        }
        this.formData.event_delimiter = raw
      }
    },
    fields() {
      if (!this.formData) {
        return []
      }
      const names = this.formData.field_names.split(',')
      return names.filter(name => !!name).map(name => ({ name }))
    }
  },
  watch: {
    show(val) {
      if (val) {
        if (this.isEditMode && this.editItem) {
          this.loadEditData()
        } else {
          this.resetForm()
        }
      }
    }
  },
  methods: {
    formatTypeOption(type) {
      if (type === 'custom') {
        return this.$t('sourceTypes.filter.custom')
      }
      return type
    },

    escapeNL(str) {
      return str.replace(/\n/g, '\\n')
    },

    unescapeNL(str) {
      return str.replace(/\\n/g, '\n')
    },

    async validateName() {
      const validation = validateSourceTypeName(
        this.formData.name,
        (key, params) => this.$t(key, params),
        this.existingSourceTypes,
        !!this.formData.id
      )
      this.validationErrors.name = validation.error
      return validation.isValid
    },

    validateConditionalFieldsForm() {
      const validation = validateConditionalFields(this.formData, (key, params) => this.$t(key, params))
      Object.keys(validation.errors).forEach(field => {
        if (this.validationErrors.hasOwnProperty(field)) {
          this.validationErrors[field] = validation.errors[field]
        }
      })
      return validation.isValid
    },

    validateCSVFieldsForm() {
      if (this.formData.type === 'csv') {
        const fields = this.fields
        if (fields.length === 0) {
          this.validationErrors.csv_fields = this.$t('sourceTypes.validation.fieldsRequired')
          return false
        }
      }
      this.validationErrors.csv_fields = ''
      return true
    },

    async validateForm() {
      const nameValid = await this.validateName()
      const conditionalValid = this.validateConditionalFieldsForm()
      const csvFieldsValid = this.validateCSVFieldsForm()
      return nameValid && conditionalValid && csvFieldsValid
    },

    clearFieldError(field) {
      this.validationErrors[field] = ''
    },

    applyCharacterLimitsToForm() {
      const result = applyCharacterLimits(this.formData)
      Object.keys(result.truncatedValues).forEach(field => {
        if (this.formData[field] !== result.truncatedValues[field]) {
          this.formData[field] = result.truncatedValues[field]
        }
      })
    },

    handleNameInput() {
      this.clearFieldError('name')
    },

    async handleNameBlur() {
      await this.validateName()
    },

    handleDescriptionInput() {
      this.descriptionCharacterCount = this.formData.description ? this.formData.description.length : 0
      this.applyCharacterLimitsToForm()
    },

    handleTypeChange() {
      if (this.formData.type === 'csv' && !this.formData.field_delimiter) {
        this.formData.field_delimiter = ','
      }
      this.validateConditionalFieldsForm()
    },

    fieldAdd() {
      if (!this.field.name || this.field.name.trim() === '') {
        this.validationErrors.field_name_input = this.$t('sourceTypes.validation.fieldNameRequired')
        return
      }
      
      // Check for whitespace in field name
      if (/\s/.test(this.field.name)) {
        this.validationErrors.field_name_input = this.$t('sourceTypes.validation.fieldNameNoSpaces')
        return
      }
      
      const names = this.fields.map(x => x.name)
      if (names.length === uniq(names.concat(this.field.name.trim())).length) {
        return
      }
      this.formData.field_names = names.concat(this.field.name.trim()).join(',')
      this.field = { name: '' }
      this.validationErrors.field_name_input = ''
      this.validateCSVFieldsForm()
    },

    fieldRemove(idx) {
      const names = this.fields.map(x => x.name)
      names.splice(idx, 1)
      this.formData.field_names = names.join(',')
      this.validateCSVFieldsForm()
    },

    handleModalClose(value) {
      // Prevent modal from closing on outside click or ESC
    },

    handleCancel() {
      // Close modal directly without confirmation
      this.closeModal()
    },

    confirmCancel() {
      this.showCancelConfirm = false
      this.closeModal()
    },

    closeModal() {
      this.$emit('update:show', false)
      this.validationErrors = {
        name: '',
        event_delimiter: '',
        field_delimiter: '',
        comment: '',
        regex_string: '',
        csv_fields: '',
        field_name_input: '',
        time_field: '',
        time_format: ''
      }
      this.descriptionCharacterCount = 0
    },

    async submit() {
      this.applyCharacterLimitsToForm()
      const isValid = await this.validateForm()
      
      if (!isValid) {
        this.$store.dispatch('NOTIFY', {
          type: 'error',
          text: this.$t('users.validation.formHasErrors')
        })
        
        // Focus on first field with error
        this.$nextTick(() => {
          const errorElement = this.$el.querySelector('.is-danger')
          if (errorElement) {
            errorElement.focus()
            errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
          }
        })
        
        return
      }

      this.isLoading = true

      try {
        const apiMethod = this.isEditMode ? 'patch' : 'post'
        const apiUrl = this.isEditMode ? `/api/v1/source-types/${this.formData.id}` : '/api/v1/source-types'
        const response = await this.$http[apiMethod](apiUrl, this.formData)

        if (response && response.data) {
          const successMessage = this.isEditMode 
            ? this.$t('sourceTypes.messages.updateSuccess')
            : this.$t('sourceTypes.messages.createSuccess')
          
          this.$store.dispatch('NOTIFY', {
            type: 'success',
            text: successMessage
          })

          this.$emit('success')
          this.closeModal()
        }
      } catch (error) {
        const errorMessage = this.isEditMode
          ? this.$t('sourceTypes.messages.updateError')
          : this.$t('sourceTypes.messages.createError')
        
        this.$store.dispatch('NOTIFY', {
          type: 'error',
          text: errorMessage
        })
      } finally {
        this.isLoading = false
      }
    },

    resetForm() {
      this.formData = {
        name: '',
        description: '',
        type: 'json',  // Default to JSON as per UC-1.15
        field_names: '',
        event_delimiter: '\\n',
        field_delimiter: ',',
        comment: '#',
        time_fill: true,
        time_field: '',
        time_format: '',
        regex_string: ''
      }
      this.validationErrors = {
        name: '',
        event_delimiter: '',
        field_delimiter: '',
        comment: '',
        regex_string: '',
        csv_fields: '',
        field_name_input: '',
        time_field: '',
        time_format: ''
      }
      this.descriptionCharacterCount = 0
    },

    loadEditData() {
      if (!this.editItem) return
      this.formData = JSON.parse(JSON.stringify(this.editItem))
      this.validationErrors = {
        name: '',
        event_delimiter: '',
        field_delimiter: '',
        comment: '',
        regex_string: '',
        csv_fields: '',
        field_name_input: '',
        time_field: '',
        time_format: ''
      }
      this.descriptionCharacterCount = this.formData.description ? this.formData.description.length : 0
    }
  }
}
</script>

<style lang="scss">
@import '@/assets/styles/dark-modal.scss';

// Additional custom styles specific to Source Type Form Modal
.dark-modal {
  .modal-box {
    padding: 1.5rem;

    .modal-box-header {
      margin-bottom: 1.25rem;
      padding-bottom: 0.75rem;

      .modal-title {
        font-size: 1.25rem;
      }
    }

    .label {
      margin-bottom: 0.35rem;
      font-size: 0.9rem;
    }

    .field {
      margin-bottom: 1rem;

      .input,
      .textarea,
      .select select {
        &.is-readonly,
        &:disabled {
          background-color: #1e2029;
          color: rgba(255, 255, 255, 0.5);
          cursor: not-allowed;
          opacity: 0.7;
        }
      }

      &.time-fields-group {
        margin-bottom: 0.25rem;
      }

      &.time-errors-group {
        margin-top: 0;
        margin-bottom: 1rem;
      }
    }

    .radio {
      color: $modal-text;
      margin-right: 1rem;
    }

    .modal-footer {
      gap: 0.75rem;
      margin-top: 1.25rem;
      padding-top: 1rem;
    }
  }
}

// Cancel Confirmation Modal Styles
.cancel-confirm-modal {
  .confirm-box {
    background-color: $modal-bg;
    border-radius: 8px;
    padding: 2rem;
    max-width: 500px;

    .confirm-header {
      margin-bottom: 1.5rem;

      h3 {
        color: $modal-text;
        font-size: 1.25rem;
        font-weight: 600;
        margin: 0;
      }
    }

    .confirm-body {
      margin-bottom: 2rem;

      p {
        color: $modal-text-secondary;
        font-size: 1rem;
        line-height: 1.5;
        margin: 0;
      }
    }

    .confirm-footer {
      display: flex;
      justify-content: flex-end;
      gap: 1rem;

      .button {
        @include dark-modal-cancel-button;

        &.is-primary {
          @include dark-modal-primary-button;
        }
      }
    }
  }
}
</style>
